import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Divider, Paper, TextField, Typography } from "@mui/material";
import { GISTMChecklist } from "../../services/api/gistm";
import { useState } from "react";
import { HidroDXEditor } from "../edit";
import { objectToHash } from "../../core";
import { RequestError, useApi } from "../../services";
import { ErrorOnDialog } from "../modals";

interface ItemProps {
  onFinish?: (checkitem: GISTMChecklist) => void;
  onCancel?: () => void;
  barragem: number;
  checkData?: GISTMChecklist;
}

export interface GISTMCheckItemModalProps extends DialogProps, ItemProps { }

export const GISTMCheckItemModal: React.FC<GISTMCheckItemModalProps> = ({ barragem, checkData, onFinish, onCancel, ...dialogProps }) => {
  const api = useApi();

  const [check, setCheck] = useState<GISTMChecklist>(checkData ?? {
    id: -1,
    idBarragem: barragem,
    principio: 1,
    requisito: 1,
    title: "",
    desc: "",
    checked: false,
    idUpdate: "",
    dataCadastro: new Date(),
    files: [],
  });
  const [error, setError] = useState<RequestError>();

  const onCancelar = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();
    ev.stopPropagation();

    setCheck({
      id: -1,
      idBarragem: -1,
      principio: 1,
      requisito: 1,
      title: "",
      desc: "",
      checked: false,
      idUpdate: "",
      dataCadastro: new Date(),
      files: [],
    });

    onCancel?.();
  };

  const onEdit = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();
    ev.stopPropagation();

    const onAdd = (value: RequestError | GISTMChecklist) => {
      if ("error" in value) {
        setError(value);
        return;
      }
      setCheck(value);
      onFinish?.(value);
    };

    if (check.id <= 0) {
      api.gistm.addChecklist(check.idBarragem, check).then(onAdd);
    } else {
      api.gistm.updateChecklist(check).then(onAdd);
    }
  };

  return <>

    {error && <ErrorOnDialog
      open={Boolean(error)}
      error={error.error}
      message={error.message}
      onClose={() => setError(undefined)}
    />}

    <Dialog {...dialogProps}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      <DialogTitle id="dialog-title">
        {check.id > 0 ? "Editar" : "Adicionar"} Checklist Item
      </DialogTitle>

      <DialogContent>
        <Divider />
        <div style={{ height: "1em" }} />

        {/* Textos e upload*/}
        <Box sx={{
          flex: 1,
          gap: "15px",
          display: "flex",
          flexDirection: "column",
        }}>
          <Box sx={{
            flex: 1,
            gap: "0.5em",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}>
            <Typography fontWeight={"bold"} fontSize={18}>
              Selecionado:
            </Typography>

            <div style={{ flex: 1 }} />

            <Checkbox
              key={objectToHash(check)} checked={check.checked ?? false}
              onChange={(_, checked) => setCheck(prev => ({ ...prev, checked }))}
              sx={{ width: "45px", height: "45px" }}
            />
          </Box>

          <Box sx={{
            flex: 1,
            gap: "0.5em",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}>
            <TextField
              fullWidth
              label="Princípio"
              value={check.principio > 0 ? check.principio : ""}
              onChange={(e) => {
                const val = parseInt(e.target.value.replaceAll(",", "."));
                if (Number.isNaN(val)) {
                  setCheck(prev => ({ ...prev, principio: 0 }));
                  return;
                }
                setCheck(prev => ({ ...prev, principio: val }));
              }}
            />
            <TextField
              fullWidth
              label="Requisito"
              value={check.requisito > 0 ? check.requisito : ""}
              onChange={(e) => {
                const val = parseInt(e.target.value.replaceAll(",", "."));
                if (Number.isNaN(val)) {
                  setCheck(prev => ({ ...prev, requisito: 0 }));
                  return;
                }
                setCheck(prev => ({ ...prev, requisito: val }));
              }}
            />
          </Box>

          <TextField
            fullWidth
            label="Título"
            value={check.title ?? ""}
            onChange={(e) => setCheck(prev => ({ ...prev, title: e.target.value }))}
          />

          {/* Descrição */}
          <Typography>Descrição:</Typography>
          <Paper elevation={1} style={{ textAlign: "justify" }}>
            <HidroDXEditor
              image={false}
              chartMap={false}
              startText={check.desc}
              onChange={(text) => setCheck({ ...check, desc: text })}
            />
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancelar} autoFocus>Cancelar</Button>
        <Button onClick={onEdit} autoFocus>{check.id > 0 ? "Editar" : "Adicionar"}</Button>
      </DialogActions>
    </Dialog>
  </>;
};



export interface GISTMCheckItemButtonProps extends ItemProps {
  title?: React.ReactNode;
  variant?: "text" | "outlined" | "contained";
}

export const GISTMCheckItemButton: React.FC<GISTMCheckItemButtonProps> = ({ title, variant, barragem, checkData, onFinish, onCancel }) => {

  const [open, setOpen] = useState(false);

  return <>
    {open && <GISTMCheckItemModal
      open={open}
      barragem={barragem}
      checkData={checkData}
      onFinish={(checkitem) => {
        setOpen(false);
        onFinish?.(checkitem);
      }}
      onCancel={() => {
        setOpen(false);
        onCancel?.();
      }}
    />}

    <Button fullWidth variant={variant} onClick={() => setOpen(true)}>
      {title ?? (checkData ? "Editar Item" : "Adicionar Item")}
    </Button>
  </>;
};